import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

/**COMPONENTS IMPORT */
import BrandLogo from "./BrandLogo";

/**DATA IMPORT */
import { site } from "../data/site";
import { navLinks, BOOK_TABLE_PATH } from "../data/navigation";
import { COMPANY, PRIVACY_PATH, REFUND_POLICY_PATH, TERMS_PATH } from "../data/legal";
import useSiteSettings from "../hooks/useSiteSettings";

const Footer = () => {
  const settings = useSiteSettings();
  const socials = [
    ["Instagram", settings.instagramUrl],
    ["Facebook", settings.facebookUrl],
  ].filter(([, url]) => url);
  const container = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <footer>
      <motion.div
        className="footer-col"
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <motion.div variants={item}>
          <BrandLogo size={110} className="footer-emblem" />
        </motion.div>

        {/* Logo */}
        <motion.p className="footer-logo" variants={item}>
          {site.name}
        </motion.p>

        {/* Description */}
        <motion.p className="section-text" variants={item}>
          Wood-fired pizza and grill inside Lyster's Pub, Harold's Cross,
          Dublin. {site.motto}.
        </motion.p>

        {/* Row */}
        <motion.div className="footer-row" variants={item}>

          {/* Left Links */}
          <motion.nav className="row-1" variants={item} aria-label="Footer">
            <p>
              {navLinks.map((link, i) => (
                <React.Fragment key={link.label}>
                  {i > 0 && " | "}
                  <Link to={link.path} className="footer-link">{link.label}</Link>
                </React.Fragment>
              ))}
              <br />
              <Link to={BOOK_TABLE_PATH} className="footer-link">Book a Table</Link>
              <br />
              <Link to={TERMS_PATH} className="footer-link">Terms &amp; Conditions</Link>
              {" | "}
              <Link to={REFUND_POLICY_PATH} className="footer-link">Refund Policy</Link>
              {" | "}
              <Link to={PRIVACY_PATH} className="footer-link">Privacy Policy</Link>
            </p>
          </motion.nav>

          {/* Empty Center for balance */}
          <div className="footer-center"></div>

          {/* Right Contact */}
          <motion.address className="row-3" variants={item}>
            <p>
              <a href={`mailto:${settings.email}`} className="contact-link">
                {settings.email}
              </a>
              {settings.phone && (
                <>
                  <br />
                  <a href={`tel:${settings.phone.replace(/[^\d+]/g, "")}`} className="contact-link">{settings.phone}</a>
                </>
              )}
              <br />
              <span className="address-text">{settings.address}</span>
              {socials.length > 0 && (
                <>
                  <br />
                  {socials.map(([label, url], i) => (
                    <React.Fragment key={label}>
                      {i > 0 && " | "}
                      <a href={url} className="footer-link" target="_blank" rel="noopener noreferrer">
                        {label}<span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    </React.Fragment>
                  ))}
                </>
              )}
            </p>
          </motion.address>

        </motion.div>

        <motion.p className="footer-copy" variants={item}>
          © {new Date().getFullYear()} {settings.name} · {site.domain}
          <br />
          <span className="footer-legal">
            {COMPANY.tradingName} is a trading name of {COMPANY.legalName} · Company Registration Number {COMPANY.croNumber}
          </span>
        </motion.p>
      </motion.div>
    </footer>
  );
};

export default Footer;
